import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Loader2, ArrowLeftRight, AlertTriangle } from 'lucide-react';
import { diagnosticsAPI } from '../../lib/api';

function fmtTs(ts) {
  if (!ts) return '-';
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
}

export function MatchingFxAttributionPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await diagnosticsAPI.matchingFxAttribution();
      setData(res.data);
    } catch {
      setError('Failed to fetch matching FX attribution.');
    } finally {
      setLoading(false);
    }
  };

  const residuals = data?.latest_run_residuals;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <ArrowLeftRight className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Matching FX Attribution</CardTitle>
          <Button
            variant="outline"
            size="sm"
            className="h-6 text-xs ml-auto"
            onClick={run}
            disabled={loading}
          >
            {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            {data ? 'Re-run' : 'Run Attribution'}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Attributes auto-matching's missing_fx_rate / fx_service_unavailable kills: a currency
          outside SUPPORTED_CURRENCIES is a steady floor across runs; an FX-service outage for one
          chunk is a spike concentrated in a single run. Read-only, does not trigger a chain run.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && <p className="text-xs text-red-600">{error}</p>}

        {data && (
          <>
            {/* (a) Currency population */}
            <div>
              <h4 className="text-xs font-medium text-muted-foreground mb-1">
                Currency Population (supported: {data.currency_population.supported_currencies.join(', ')})
              </h4>
              {Object.entries(data.currency_population.by_collection).map(([coll, info]) => (
                <div key={coll} className="mb-2">
                  <span className="text-xs font-mono font-medium capitalize">{coll}</span>
                  <div className="border rounded mt-1 overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="text-left px-2 py-1 font-medium">Currency</th>
                          <th className="text-right px-2 py-1 font-medium">Count</th>
                          <th className="text-center px-2 py-1 font-medium">Supported</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(info.by_currency).map(([cur, count]) => {
                          const unsupported = info.unsupported_currencies.includes(cur);
                          return (
                            <tr key={cur} className="border-b last:border-0">
                              <td className="px-2 py-1 font-mono">{cur === '__missing__' ? '(missing)' : cur}</td>
                              <td className="px-2 py-1 text-right">{count.toLocaleString()}</td>
                              <td className="px-2 py-1 text-center">
                                {cur === '__missing__' ? '-' : (
                                  <Badge variant={unsupported ? 'destructive' : 'default'} className="text-[10px]">
                                    {unsupported ? 'no' : 'yes'}
                                  </Badge>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {info.unsupported_currencies.length > 0 && (
                    <div className="flex items-center gap-1 mt-1 text-[11px] text-red-600">
                      <AlertTriangle className="h-3 w-3" />
                      Unsupported: {info.unsupported_currencies.join(', ')} -- a permanent, every-run
                      missing_fx_rate floor for any cross-currency pair involving these.
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* (c) Latest run residuals -- side by side */}
            {residuals && (
              <div className="border-t pt-3">
                <h4 className="text-xs font-medium text-muted-foreground mb-2">
                  Latest Run Residuals {residuals.chain_id ? `(${residuals.chain_id})` : ''}
                </h4>
                {residuals.note ? (
                  <p className="text-xs text-muted-foreground">{residuals.note}</p>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-2 rounded border bg-muted/30">
                      <div className="text-[11px] text-muted-foreground">missing_fx_rate</div>
                      <div className="text-lg font-mono font-medium">{residuals.missing_fx_rate}</div>
                      <div className="text-[10px] text-muted-foreground">genuinely no rate anywhere</div>
                    </div>
                    <div className="p-2 rounded border bg-muted/30">
                      <div className="text-[11px] text-muted-foreground">fx_service_unavailable</div>
                      <div className="text-lg font-mono font-medium">{residuals.fx_service_unavailable}</div>
                      <div className="text-[10px] text-muted-foreground">FX service failed resolving it</div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* (b) Chain run history */}
            <div className="border-t pt-3">
              <h4 className="text-xs font-medium text-muted-foreground mb-1">
                Chain Run History (most recent first)
              </h4>
              <div className="border rounded overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-2 py-1 font-medium">Chain</th>
                      <th className="text-left px-2 py-1 font-medium">Status</th>
                      <th className="text-left px-2 py-1 font-medium">Started</th>
                      <th className="text-right px-2 py-1 font-medium">Pairs</th>
                      <th className="text-right px-2 py-1 font-medium">missing_fx_rate</th>
                      <th className="text-right px-2 py-1 font-medium">fx_service_unavailable</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.chain_run_history.map((run) => (
                      <tr key={run.chain_id} className="border-b last:border-0">
                        <td className="px-2 py-1 font-mono">{run.chain_id}</td>
                        <td className="px-2 py-1">{run.status}</td>
                        <td className="px-2 py-1 font-mono">{fmtTs(run.started_at)}</td>
                        <td className="px-2 py-1 text-right">{run.pairs_evaluated?.toLocaleString?.() ?? '-'}</td>
                        <td className="px-2 py-1 text-right font-mono">{run.missing_fx_rate}</td>
                        <td className="px-2 py-1 text-right font-mono">{run.fx_service_unavailable}</td>
                      </tr>
                    ))}
                    {data.chain_run_history.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-2 py-2 text-center text-muted-foreground">
                          No auto-match chain runs recorded yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
